import { utils } from '@makeship/core';
import { ProductStage, ProductTag, BadgeText } from '../../types/common';
import { FontAwesomeIcon } from '../../ui/types/fontAwesomeIcons';
import { ProductCardSpecsProps } from '../../ui/molecules/ProductCardSpecs';
import {
  hasProductTag,
  getProductIDFromShopifyGID,
  shouldShowProgressBar,
  isSizedApparelProduct,
  calculateTotalInventorySold,
  getVariantShipDateInfo,
  isCurrentDateBeforeGivenMetafieldDate,
  parseVariantMetafields,
} from '../../utils/product';
import { BadgeColor } from '../../ui/atoms/Badge/badge';
import { getPetitionPledgeCountByExternalProductID } from '../../api/storefront/petition';
import { logSentryError } from '../../utils/errors';

export const fetchProductTotalSold = async (
  product: Shopify.Product,
  isPetition: boolean,
  isSuccessfulPetitionNotLive: boolean,
): Promise<number> => {
  try {
    // Validate input
    if (!product) {
      logSentryError(new Error('No product provided'), 'fetchProductTotalSold', 'Product parameter is missing');
      return 0;
    }

    const shopifyProductInventory = Math.abs(product.totalInventory ?? 0);

    if (isPetition || isSuccessfulPetitionNotLive) {
      try {
        const productId = getProductIDFromShopifyGID(product.id);
        const response = await getPetitionPledgeCountByExternalProductID(productId);
        const pledgeCount = response?.data;

        if (pledgeCount !== null && pledgeCount !== undefined) {
          return pledgeCount;
        }
      } catch (error) {
        logSentryError(error, 'fetchProductTotalSold', 'Failed to fetch petition pledge count');
      }

      return shopifyProductInventory;
    }

    const currentTotalSold = await calculateTotalInventorySold(product);
    return currentTotalSold ?? shopifyProductInventory;
  } catch (error) {
    logSentryError(error, 'fetchProductTotalSold', 'Unexpected error in fetchProductTotalSold');
    // Fallback to safe default
    return Math.abs(product?.totalInventory ?? 0);
  }
};

type GetProductCardSpecsParams = {
  product: Shopify.Product;
  totalSold: number;
  stage: ProductStage;
  metafields: Metafields;
  isLimited: boolean;
  isPetition: boolean;
  isSuccessfulPetitionNotLive: boolean;
  moq: number;
  isSoldOut: boolean;
  hideUnits: boolean;
  showLowStock?: boolean;
  showReadyToShip?: boolean;
  showPreOrder?: boolean;
};

export const getProductCardSpecs = ({
  product,
  totalSold,
  stage,
  metafields,
  isLimited,
  isPetition,
  moq,
  isSoldOut,
  hideUnits,
  showLowStock,
  showReadyToShip,
  showPreOrder,
}: GetProductCardSpecsParams): ProductCardSpecsProps => {
  // Single spec states (priority order)
  if (stage === ProductStage.ComingSoon && metafields.launch) {
    const launchDate = utils.formatShipDate(metafields.launch);
    return {
      progressBar: false as const,
      spec: {
        icon: FontAwesomeIcon.Calendar,
        text: `Drops ${launchDate}` as const,
      },
    };
  }

  if (showLowStock) {
    return {
      progressBar: false as const,
      spec: {
        icon: FontAwesomeIcon.CircleExclamation,
        text: `Only ${formatNumberWithCommas(totalSold)} left in stock`,
      },
    };
  }

  if (isSoldOut) {
    return {
      progressBar: false as const,
      spec: {
        icon: FontAwesomeIcon.FaceFrown,
        text: 'Sold Out' as const,
      },
    };
  }

  // Check if product has variant-level ship dates
  if (product.variants?.edges?.length > 0) {
    const { hasDifferentShipDates, allVariantsReadyToShip, hasVariantShipDates, hasReadyToShip, hasPreOrder } =
      getVariantShipDateInfo(product);

    if (hasVariantShipDates) {
      // All variants are ready to ship (dates in past or same)
      if (allVariantsReadyToShip) {
        return {
          progressBar: false as const,
          spec: {
            icon: FontAwesomeIcon.Check,
            text: 'In stock and ready-to-ship',
          },
        };
      }

      // Check if all variants have the same future ship date
      if (!hasDifferentShipDates) {
        // Get the ship date from the first variant
        const { shipByDate } = parseVariantMetafields(product.variants.edges[0].node.metafields);

        if (shipByDate && isCurrentDateBeforeGivenMetafieldDate(shipByDate)) {
          const shipDateFormatted = utils.formatShipDate(shipByDate);
          return {
            progressBar: false as const,
            spec: {
              icon: FontAwesomeIcon.Ticket,
              text: `Pre-order, est. ships ${shipDateFormatted}`,
            },
          };
        }
      }

      // Different ship dates or mix of R2S and pre-order
      if (hasDifferentShipDates) {
        // Mixed or different future dates - show limited quantities
        if ((hasReadyToShip && hasPreOrder) || (!allVariantsReadyToShip && hasDifferentShipDates)) {
          return {
            progressBar: false as const,
            spec: {
              icon: FontAwesomeIcon.CircleExclamation,
              text: 'Limited quantities' as const,
            },
          };
        }
      }
    }
  }

  // Fall back to product-level logic when no variant dates exist
  if (showReadyToShip) {
    return {
      progressBar: false as const,
      spec: {
        icon: FontAwesomeIcon.Check,
        text: 'In stock and ready-to-ship',
      },
    };
  }

  if (showPreOrder) {
    const shipDate = metafields.delivery ? utils.formatShipDate(metafields.delivery) : '';
    return {
      progressBar: false as const,
      spec: {
        icon: FontAwesomeIcon.Ticket,
        text: shipDate ? `Pre-order, est. ships ${shipDate}` : 'Pre-order',
      },
    };
  }

  if (isLimited) {
    return {
      progressBar: false as const,
      spec: {
        icon: FontAwesomeIcon.Check,
        text: `In stock but only ${formatNumberWithCommas(moq)} made`,
      },
    };
  }

  // Progress bar state
  const progressBarValue = Math.floor((totalSold / moq) * 100);
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const isLimitedStock = isLimited || isEvergreen;
  const shouldShowProgress = shouldShowProgressBar(stage, getProductIDFromShopifyGID(product.id), isLimitedStock);

  if (shouldShowProgress) {
    const getStatsText = (): string => {
      if (hideUnits && totalSold >= moq) {
        return `${moq}+ Sold`;
      }
      return `${formatNumberWithCommas(totalSold)} Sold`;
    };

    const getTimeText = () => {
      const { enddate, petitionEndDate } = metafields;
      const targetDate = stage === 'petition' ? petitionEndDate : enddate;

      if (!targetDate) return 'Ltd Time';

      const now = new Date();
      const end = utils.parseDate(targetDate).toDate();
      const diffMs = end.getTime() - now.getTime();

      if (diffMs < 0) return '0d Left';

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (days > 0) return `${days}d Left`;

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      if (hours > 0) return `${hours}h Left`;

      const minutes = Math.floor(diffMs / (1000 * 60));
      return `${minutes}m Left`;
    };

    const soldCount = totalSold;

    return {
      progressBar: true as const,
      progressBarValue,
      progressBarLeftSpec: isPetition
        ? { icon: FontAwesomeIcon.Users, text: `${formatNumberWithCommas(soldCount)} Fans` }
        : { icon: FontAwesomeIcon.RocketLaunch, text: getStatsText() },
      progressBarRightSpec: {
        icon: FontAwesomeIcon.Clock,
        text: getTimeText(),
      },
    };
  }

  // Default fallback
  return {
    progressBar: false as const,
    spec: {
      icon: FontAwesomeIcon.CircleExclamation,
      text: 'Limited quantities' as const,
    },
  };
};

export const getBadgeSpecs = (
  sale?: boolean | undefined,
  shouldShowFreeGiftBadge?: boolean,
  hasAchievedStretchGoals?: boolean,
): Array<{ badgeColor: BadgeColor; badgeText: BadgeText }> | undefined => {
  const badges: Array<{ badgeColor: BadgeColor; badgeText: BadgeText }> = [];

  if (sale) {
    badges.push({
      badgeColor: 'red',
      badgeText: 'Sale' as BadgeText,
    });
  }

  if (shouldShowFreeGiftBadge) {
    badges.push({
      badgeColor: 'blurple',
      badgeText: (hasAchievedStretchGoals ? 'Free Gift' : 'Unlock Gift') as BadgeText,
    });
  }

  return badges.length > 0 ? badges : undefined;
};

const formatNumberWithCommas = (num: number): string => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const getProductImageSrc = (product: Shopify.Product): string =>
  product.images.edges[0]?.node?.transformedSrc || '';

export const getProductSubtitle = (metafields: Metafields): string => metafields.creator || '';

export const getCtaText = (stage: ProductStage, product: Shopify.Product): string => {
  if (isSizedApparelProduct(product) && stage === ProductStage.Live) {
    return 'View Product';
  }
  switch (stage) {
    case ProductStage.Past:
      return 'Bring it Back';
    case ProductStage.ComingSoon:
      return 'Notify Me';
    case ProductStage.Petition:
      return 'View Product';
    default:
      return 'Add to Cart';
  }
};

type CtaClickHandler = ((e?: React.MouseEvent) => void) | undefined;

export const MODAL_CTA_NOTIFY_ME = 'Notify Me';
export const MODAL_CTA_BRING_IT_BACK = 'Bring it Back';

export const getModalData = (ctaText: string, productTitle: string, creator: string) => {
  const lowerCtaText = ctaText.toLowerCase();
  const ctaTag = lowerCtaText.replace(/\s+/g, '-');
  const tags = `subscribed-product, ${productTitle}, ${creator}, ${ctaTag}, product-card`;

  if (lowerCtaText === MODAL_CTA_NOTIFY_ME.toLowerCase()) {
    return {
      tags,
      title: 'Subscribe for Updates',
      description: 'Enter your email to get notified when this product goes live',
    };
  }

  if (lowerCtaText === MODAL_CTA_BRING_IT_BACK.toLowerCase()) {
    return {
      tags,
      title: 'Want to see this product return?',
      description: `Let ${creator} know you want to bring it back!`,
    };
  }

  return {
    tags,
    title: 'Subscribe for Updates',
    description: 'Enter your email to subscribe for updates',
  };
};

export const getOnCtaClick = (
  stage: ProductStage,
  product: Shopify.Product,
  isSoldOut: boolean,
  isPetition: boolean,
  handleAddToCart: (e?: React.MouseEvent) => void,
  handleSubscribe: (ctaText: string, e?: React.MouseEvent) => void,
  handleViewProduct?: () => void,
): CtaClickHandler => {
  // Prioritize: Sold out products should show subscribe modal
  if (isSoldOut) {
    return (e) => handleSubscribe(MODAL_CTA_BRING_IT_BACK, e);
  }

  // Prioritize: Coming soon products should show subscribe modal
  if (stage === ProductStage.ComingSoon) {
    return (e) => handleSubscribe(MODAL_CTA_NOTIFY_ME, e);
  }

  // Petitions and sized apparel products should navigate to product page
  // Fire analytics event but don't prevent navigation
  if (isPetition || isSizedApparelProduct(product)) {
    return handleViewProduct;
  }

  // Only show add to cart for live products that are available
  if (stage === ProductStage.Live && product.availableForSale) {
    return handleAddToCart;
  }

  // Default: fire analytics event but allow navigation to product page
  return handleViewProduct;
};
